import { Course, SectionData } from '@/types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import QuickAccessSection from './QuickAccessSection';

interface QuickAccessProps {
    course: Course;
}

interface SectionGroup {
    groupName: string | null;
    sections: SectionData[];
}

const QuickAccess: React.FC<QuickAccessProps> = ({ course }) => {
    const { t } = useTranslation();

    // Filter sections: not hidden from quick access AND has an icon image
    const visibleSections = course.sections.filter(
        (section) => !section.hide_from_quick_access && section.icon_url
    );

    // Group sections by quick_access_group while preserving order
    const groupedSections = useMemo(() => {
        const groups: SectionGroup[] = [];
        const seenGroups = new Map<string | null, number>();

        visibleSections.forEach((section) => {
            const groupName = section.quick_access_group || null;
            const existingGroupIndex = seenGroups.get(groupName);

            if (existingGroupIndex !== undefined) {
                groups[existingGroupIndex].sections.push(section);
            } else {
                seenGroups.set(groupName, groups.length);
                groups.push({
                    groupName,
                    sections: [section],
                });
            }
        });

        return groups;
    }, [visibleSections]);

    if (visibleSections.length === 0) {
        return null;
    }

    return (
        <div className="mb-8 overflow-hidden rounded-2xl bg-gradient-to-br from-slate-50 to-gray-100 p-6 shadow-sm ring-1 ring-gray-200/50">
            <div className="mb-5 flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/10">
                    <svg
                        className="h-4 w-4 text-emerald-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                    </svg>
                </div>
                <h2 className="text-lg font-semibold text-gray-800">
                    {t('course.quickAccess', 'Acceso rápido')}
                </h2>
            </div>

            <div className="space-y-6">
                {groupedSections.map((group, groupIndex) => (
                    <div key={group.groupName || `ungrouped-${groupIndex}`}>
                        {group.groupName && (
                            <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-gray-600">
                                {group.groupName}
                            </h3>
                        )}
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                            {group.sections.map((section) => (
                                <QuickAccessSection
                                    key={section.id}
                                    section={section}
                                    courseSlug={course.slug}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default QuickAccess;
